import { appendUtmsToPath } from "@/utils/utm";
import { completeSignup } from "@/utils/signup";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "@/custom.css";
import ReCAPTCHA from "react-google-recaptcha";
import { Helmet } from "react-helmet-async";
import { cn } from "@/lib/utils";
import { checkCreateProspectRateLimit } from "@/utils/createProspectRateLimiter";
import logger from "@/utils/logger";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { landingAfterSignIn } from "@/utils/todays-tips-ready";

// reCAPTCHA site keys
const RECAPTCHA_SCORE_KEY =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
  "6LdewqYrAAAAACyovzEoE3tUtmtNi4ZgLTnqzTnn";
// reCAPTCHA v2 Checkbox key for fallback when v3 score is low
const RECAPTCHA_CHECKBOX_KEY =
  import.meta.env.VITE_RECAPTCHA_CHECKBOX_KEY ||
  "6Lexl10sAAAAAMFs_DawLbU7xGg9ogJcgHy3dWDH";

// Utility functions (same as AuthModal)
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function ErrorMessage({ message, type = "field" }) {
  return (
    <div className="flex items-center gap-2">
      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
      <p
        className={
          type === "field" ? "text-xs text-red-600" : "text-sm text-red-600"
        }
      >
        {message}
      </p>
    </div>
  );
}

const PLANS = [
  {
    id: "free",
    name: "Lexi Social",
    description: "Get started with the essentials",
    price: "FREE",
    features: ["2 Race-Day tips", "All AU races", "Full results history"],
  },
  {
    id: "day-pass",
    name: "Lexi 7-Day Pass",
    description: "Try premium features",
    price: 9.0,
    period: "once-off",
    features: ["Lexi Pro full access pass for 7 days"],
  },
  {
    id: "pro",
    name: "Lexi Pro",
    description: "Most popular choice",
    price: 29.95,
    period: "/month",
    features: [
      "4 Early Mail / Race-Day / Late Mail tips",
      "Lexi Chat",
      "History",
    ],
    isPopular: true,
    badge: "Popular",
  },
];

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const hideParam = searchParams.get("hide");

  const visiblePlans = useMemo(() => {
    const hideList = hideParam ? hideParam.split(",").map((s) => s.trim()) : [];
    const filtered = PLANS.filter((p) => {
      // If plan is explicitly requested via 'plan', it takes precedence over 'hide'
      if (planParam === p.id) return true;
      return !hideList.includes(p.id);
    });

    return filtered.length > 0 ? filtered : PLANS;
  }, [planParam, hideParam]);

  const initialPlan = useMemo(() => {
    if (planParam && visiblePlans.some((p) => p.id === planParam)) {
      return planParam;
    }
    return visiblePlans[0]?.id || "pro";
  }, [planParam, visiblePlans]);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [errors, setErrors] = useState({});
  const [expandedPlans, setExpandedPlans] = useState({});

  // reCAPTCHA ref for invisible reCAPTCHA (Score)
  const recaptchaScoreRef = useRef(null);
  // reCAPTCHA ref for visible reCAPTCHA (Checkbox/Challenge)
  const recaptchaChallengeRef = useRef(null);

  // State to control whether to show the visible challenge
  const [showChallenge, setShowChallenge] = useState(false);

  const togglePlanDetails = (planId) => {
    setExpandedPlans((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));
  };

  const handleContactSubmit = async () => {
    // Validate form
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!selectedPlan) {
      newErrors.plan = "Please select a plan";
      setErrors(newErrors);
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check client-side rate limit
    const rateLimitCheck = checkCreateProspectRateLimit();
    if (!rateLimitCheck.allowed) {
      setErrors({
        submit:
          rateLimitCheck.message ||
          "Too many signup attempts. Please try again later.",
      });
      setErrors({
        submit:
          rateLimitCheck.message ||
          "Too many signup attempts. Please try again later.",
      });
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token for bot protection (mandatory)
      let recaptchaToken = null;

      try {
          if (showChallenge) {
            // Checkbox mode - reset the invisible one first
            if (recaptchaScoreRef.current) {
              try {
                recaptchaScoreRef.current.reset();
              } catch (e) {
                // Ignore reset errors
              }
            }

            if (recaptchaChallengeRef.current) {
              recaptchaToken = recaptchaChallengeRef.current.getValue();

              if (!recaptchaToken) {
                setErrors({ submit: "Please complete the security check." });
                setLoading(false);
                return;
              }
            }
          } else {
            // Score mode (Invisible)
            if (recaptchaScoreRef.current) {
              recaptchaToken = await recaptchaScoreRef.current.executeAsync();
              recaptchaScoreRef.current.reset();
            }
          }
        } catch (recaptchaError) {
          logger.error("reCAPTCHA error:", recaptchaError);
          setLoading(false);
          setErrors({
            submit: "Security verification failed. Please try again.",
          });
          setErrors({
            submit: "Security verification failed. Please try again.",
          });
          return;
        }

      // Enforce reCAPTCHA token requirement
      if (!recaptchaToken) {
        setLoading(false);
        setErrors({
          submit: showChallenge
            ? "Please complete the security check."
            : "Security verification is required. Please enable JavaScript and try again.",
        });
        return;
      }


      const response = await fetch("/api/create-prospect", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          disabledOtp: true,
          with: "auth",
          recaptchaToken, // Include reCAPTCHA token for bot protection
          // Send the key we used so the backend validates against the correct one
          siteKey: showChallenge ? RECAPTCHA_CHECKBOX_KEY : RECAPTCHA_SCORE_KEY,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const destination = await completeSignup(result, selectedPlan);
        // /home instead of /todays-tips until today's tips are out (mornings only)
        const landing = await landingAfterSignIn(destination);
        setLoading(false);
        navigate(appendUtmsToPath(landing), { replace: true });
      } else {
        // Handle "requires_challenge" error
        if (result.error === "requires_challenge") {
          logger.warn("Low reCAPTCHA score, requiring visible challenge");
          setLoading(false);
          setShowChallenge(true); // Switch to visible mode
          setErrors({
            submit: "Please complete the security check below to continue.",
          });
          return;
        }

        setErrors({ submit: result.error || "Failed to create account" });
        setLoading(false);
      }
    } catch (error) {
      logger.error("Error creating prospect:", error);
      setErrors({ submit: "An error occurred. Please try again." });
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        handleBack();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    window.scrollTo(0, document.documentElement.scrollHeight);
  }, []);

  return (
    <>
      <Helmet>
        <title>Sign Up | Lexi Tips</title>
        <meta
          name="description"
          content="Create your account to get started with Lexi Tips"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl">
          {/* Header */}
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-3 text-gray-600 py-3 px-6 hover:text-brand-pink transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-xs font-medium">Back</span>
            </button>
          </div>

          {/* Two-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 auto-rows-max lg:auto-rows-auto">
            {/* Right Column - Form */}
            <div className="min-w-0 order-2 lg:order-2">
              <Card className="border-0 shadow-lg bg-white h-full">
                <div className="p-6 md:p-8 space-y-6">
                  {/* Title Section */}
                  <div className="space-y-2">
                    <h1 className="text-2xl md:text-3xl font-bold text-brand-pink">
                      Get Lexi&apos;s Tips Now
                    </h1>
                    <p className="text-sm text-gray-600">
                      Create your account to get started
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="space-y-4">
                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="signup-email"
                        className="text-sm font-semibold text-gray-700"
                      >
                        Email <span className="text-brand-pink">*</span>
                      </Label>
                      <Input
                        autoFocus
                        id="signup-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setErrors((prev) => ({ ...prev, email: "" }));
                        }}
                        onBlur={() => {
                          setErrors((prev) => {
                            if (!email)
                              return { ...prev, email: "Email is required" };
                            if (!validateEmail(email))
                              return {
                                ...prev,
                                email: "Please enter a valid email address",
                              };
                            return prev;
                          });
                        }}
                        className={cn(
                          "h-10 border bg-white text-sm",
                          errors.email ? "border-red-500" : "border-gray-300",
                        )}
                      />
                      <p className="text-xs text-gray-500">
                        Used for login and tip updates.
                      </p>
                      {errors.email && (
                        <div className="mt-2">
                          <ErrorMessage message={errors.email} type="field" />
                        </div>
                      )}
                    </div>

                    {/* Mobile Field */}

                  </div>

                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3 items-center">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700 leading-snug">
                        {errors.submit}
                      </p>
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={handleContactSubmit}
                      disabled={loading}
                      className={cn(
                        "w-full h-11 font-semibold rounded-lg text-sm text-white transition-all",
                        "bg-[linear-gradient(180deg,_#C8A8E4_0%,_#C480FF_3.37%,_#E829A1_94.71%,_#BB92DA_100%)]",
                        "hover:brightness-105 active:brightness-95",
                        "disabled:opacity-60 disabled:cursor-not-allowed",
                      )}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </span>
                      ) : selectedPlan === "free" ? (
                        "Get Tips Now"
                      ) : (
                        "Continue to secure checkout"
                      )}
                    </Button>
                    <div
                      className={cn(
                        "flex justify-center items-center gap-2 pt-[6px]",
                        selectedPlan === "free" ? "hidden" : "",
                      )}
                    >
                      <span
                        className="relative h-[25px] w-[24px]"
                        aria-hidden="true"
                      >
                        <ShieldCheck
                          className="absolute inset-0 h-[25px] w-[24px]"
                          fill="#f30099"
                          stroke="#f30099"
                        />
                        <Check
                          className="absolute left-1/2 top-[52%] h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 text-white"
                          strokeWidth={3}
                        />
                      </span>
                      <span className="text-[14px] font-semibold leading-[29px] text-brand-pink">
                        Cancel anytime
                      </span>
                    </div>

                    <div className="text-center">
                      <span className="text-xs text-gray-500">or</span>
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => navigate("/login")}
                      className="w-full h-10 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                    >
                      Sign In
                    </Button>
                  </div>

                  {/* ReCAPTCHA Components */}

                  {/* 2. Visible Checkbox (Fallback) */}
                  {showChallenge ? (
                    <div className="mt-4 flex justify-center">
                      <ReCAPTCHA
                        ref={recaptchaChallengeRef}
                        sitekey={RECAPTCHA_CHECKBOX_KEY}
                      />
                    </div>
                  ) : (
                    <ReCAPTCHA
                      ref={recaptchaScoreRef}
                      size="invisible"
                      sitekey={RECAPTCHA_SCORE_KEY}
                      badge="bottomright"
                    />
                  )}

                  {/* Trust Footer */}
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xxs text-gray-500 leading-relaxed text-center">
                      This site is protected by reCAPTCHA and the{" "}
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-pink hover:underline"
                      >
                        Google Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-pink hover:underline"
                      >
                        Terms of Service
                      </a>{" "}
                      apply.
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Left Column - Plan Selection */}
            <div className="min-w-0 order-1 lg:order-1">
              <Card
                className="relative border-0 shadow-xl h-full overflow-hidden gradient-bg"
                // style={{
                //   background:
                //     "conic-gradient(from 180deg at 50% 50%, #FFFFFF -90.1deg, #FFFFFF 88.94deg, #BB92DA 126.08deg, #FACCFF 156.39deg, #F6C1F1 201.75deg, #EEACD8 228.87deg, #FFFFFF 269.9deg, #FFFFFF 448.94deg)",
                // }}
              >
                <div className="p-6 md:p-8 space-y-6 flex flex-col h-full">
                  {/* Title Section */}
                  <div className="space-y-1 z-50">
                    <h2 className="text-xl md:text-2xl font-bold text-brand-pink">
                      Choose your plan
                    </h2>
                    <p className="text-sm text-gray-600 hidden sm:block">
                      Pick the option that works best for you
                    </p>
                  </div>

                  {/* Plan Selection */}
                  <div className="flex-1 z-50">
                    <div className="space-y-4">
                      {visiblePlans.map((plan) => {
                        const isSelected = selectedPlan === plan.id;
                        const isExpanded = expandedPlans[plan.id];

                        return (
                          <div key={plan.id} className="flex flex-col gap-1">
                            <div
                              onClick={() => setSelectedPlan(plan.id)}
                              className={cn(
                                "relative flex items-start sm:items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all border backdrop-blur-md",
                                plan.name === "Lexi Pro"
                                  ? "bg-[conic-gradient(from_360deg_at_100%_-50%,#FFFFFF_-90.1deg,#FFFFFF_88.94deg,#BB92DA_126.08deg,#FACCFF_156.39deg,#F6C1F1_201.75deg,#EEACD8_228.87deg,#FFFFFF_269.9deg,#FFFFFF_448.94deg)]"
                                  : "bg-white/90",
                                isSelected
                                  ? "border-brand-pink shadow-lg ring-2 ring-brand-pink/30"
                                  : plan.name === "Lexi Pro"
                                    ? "border-brand-pink/40 hover:border-brand-pink/40 shadow-md"
                                    : "border-gray-200 hover:border-brand-pink/40 shadow-md",
                              )}
                            >
                              {/* Radio */}
                              <div
                                className={cn(
                                  "mt-0 sm:mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                  isSelected
                                    ? "border-brand-pink"
                                    : "border-gray-300",
                                )}
                              >
                                {isSelected && (
                                  <div className="w-2.5 h-2.5 rounded-full bg-brand-pink" />
                                )}
                              </div>

                              {/* Plan Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0 sm:mb-1">
                                  <div className="flex items-start">
                                    <span className="font-semibold text-sm text-gray-900">
                                      {plan.name}
                                    </span>
                                    {/* Mobile Toggle Button */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        togglePlanDetails(plan.id);
                                      }}
                                      className="text-xxs font-medium text-brand-pink hover:text-brand-pink/80 transition-colors flex items-center gap-1 self-end px-2 py-1 sm:hidden group"
                                    >
                                      {isExpanded ? <>Hide</> : <>Read more</>}
                                    </button>
                                  </div>

                                  {plan.badge && (
                                    <Badge className="bg-brand-pink/80 text-white text-xs px-2 py-0.5 absolute -top-3 z-10 -right-3">
                                      {plan.badge}
                                    </Badge>
                                  )}
                                </div>

                                {/* Desktop View: Always visible | Mobile View: Toggleable */}
                                <div
                                  className={cn(
                                    "space-y-2 transition-all duration-300 overflow-hidden",
                                    isExpanded
                                      ? "block mt-2 opacity-100"
                                      : "hidden sm:block",
                                  )}
                                >
                                  <p className="text-xs text-gray-600">
                                    {plan.description}
                                  </p>

                                  <ul className="space-y-1.5">
                                    {plan.features.map((feature, idx) => (
                                      <li
                                        key={idx}
                                        className="flex items-start gap-2 text-xs text-gray-700"
                                      >
                                        <Check className="w-3.5 h-3.5 text-brand-pink mt-0.5 flex-shrink-0" />
                                        <span>{feature}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              {/* Price */}
                              <div className="ml-3 text-right flex-shrink-0">
                                <div
                                  className={cn(
                                    "font-bold text-gray-900 text-sm",
                                    plan.id === "free"
                                      ? "text-brand-pink"
                                      : "text-gray-900",
                                  )}
                                >
                                  {typeof plan.price === "number"
                                    ? `$${plan.price.toFixed(2)}`
                                    : plan.price}
                                </div>
                                {plan.period && (
                                  <div className="text-xs text-gray-500 hidden sm:block">
                                    {plan.period}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
